import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import { Loader } from "../../Common/Loader";
import { X, Loader as LoaderIcon, Eye, EyeOff } from "lucide-react";
import { Common } from "helpers/common";
import { ToastContainer } from "react-toastify";
import DeleteModal from "Common/DeleteModal";
import Drawer from "Common/Components/Drawer";
import { addUserAdmin, updateUserAdmin } from "helpers/backend_helper";
import { useAuth } from "hooks/useStore";

type Props = { onHide: () => void; show: boolean, data: any; onSave: (result: any) => void };
export const AddUser: React.FC<Props> = ({ onHide, show, data, onSave }) => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { user } = useAuth();
  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const uiHelper = new Common();

  // Role options - you might want to fetch these from an API
  const roleOptions = [
    { value: 1, label: "Admin" },
    { value: 2, label: "Teacher" },
    { value: 3, label: "Parent" },
  ];

  useEffect(() => {
    if (data?.userId) {
      setIsEdit(true);
      loadUser();
    } else {
      setIsEdit(false);
      setUserData({});
      validation.resetForm();
    }
  }, [data]);

  const loadUser = async () => {
    setIsLoading(true);
    try {
      setUserData(data);

      // Set form values
      validation.setValues({
        userId: data.userId,
        loginId: data.loginId,
        password: "", // Don't populate password for security
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        phone: data.phone || '',
        roleId: data.roleId || 1,
        isActive: data.isActive !== undefined ? data.isActive : true
      });
    } catch (error) {
      console.error("Error loading user:", error);
      uiHelper.showErrorMessage("Error loading user data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawer = () => { onHide(); }

  // validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      userId: (data && data.userId) || 0,
      loginId: (data && data.loginId) || "",
      password: "",
      firstName: (data && data.firstName) || "",
      lastName: (data && data.lastName) || "",
      email: (data && data.email) || "",
      phone: (data && data.phone) || "",
      roleId: (data && data.roleId) || 1,
      isActive: (data && data.isActive !== undefined) ? data.isActive : true
    },
    validationSchema: Yup.object({
      loginId: Yup.string().required("Please enter login ID"),
      password: isEdit 
        ? Yup.string() // Password optional for edit
        : Yup.string().min(6, "Password must be at least 6 characters").required("Please enter password"),
      firstName: Yup.string().required("Please enter first name"),
      lastName: Yup.string().required("Please enter last name"),
      email: Yup.string().email("Please enter a valid email").required("Please enter email"),
      phone: Yup.string(),
      roleId: Yup.number().required("Please select role")
    }),
    onSubmit: (values) => {
      handleSave(values);
    }
  });

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      const formData = {
        ...values,
        createdBy: user?.userId ?? 1,
        updatedBy: user?.userId ?? 1
      };

      // Remove password if empty during edit
      if (isEdit && !formData.password) {
        delete formData.password;
      }

      let response;
      response = isEdit ? await updateUserAdmin(formData) : await addUserAdmin(formData);
      
      uiHelper.showSuccessMessage(
        `User has been successfully saved.`
      );

      onSave(response.data);
      //onHide();
    } catch (error) {
      console.error("Error saving user:", error);
      uiHelper.showErrorMessage("Error saving user data");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onHide();
  };

  return (
    <React.Fragment>
      <Drawer show={show} onHide={handleDrawer} id="userSidePenal" drawer-end="true" className="fixed inset-y-0 flex flex-col  
      transition-transform duration-600 ease-in-out transform bg-white shadow 
     dark:bg-zink-600 ltr:right-0 rtl:left-0  w-5/6 flex  z-drawer">

        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
          <div className="grow">
            <h5 className="mb-0 text-16">{isEdit ? "Edit" : "Add"} User</h5>
          </div>
          <div className="shrink-0">
            <div className="flex items-center justify-end gap-[200px]">
              <div className="flex items-center gap-2 group">

              </div>
              <Drawer.Header data-drawer-close="userSidePenal" className="transition-all duration-150 ease-linear text-slate-500 hover:text-slate-800">
                <X className="size-4"></X>
              </Drawer.Header>
            </div>
          </div>
        </div>

        <Drawer.Body className="h-full p-0 overflow-y-auto">
          <div className="flex h-full">
            {/* Form Section - 100% width */}
            <div className="w-full h-full overflow-y-auto">
              <div className="h-full overflow-y-auto product-list">
                <div className="row">
                  <div className="col-xl-12">
                    <div className="card">
                      <div className="card-body">
                        {isLoading ? (
                          <Loader />
                        ) : (
                          <form
                            id="userForm"
                            className="needs-validation"
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            <div className="p-4">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                
                                {/* LEFT PANEL - Personal Information */}
                                <div className="space-y-6">
                                  
                                  {/* Personal Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        👤 Personal Information
                                      </h6>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="form-label" htmlFor="firstName">
                                          First Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="firstName"
                                          name="firstName"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.firstName && validation.errors.firstName ? "is-invalid" : ""}`}
                                          placeholder="Enter first name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.firstName}
                                        />
                                        {validation.touched.firstName && validation.errors.firstName ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.firstName?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="lastName">
                                          Last Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="lastName"
                                          name="lastName"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.lastName && validation.errors.lastName ? "is-invalid" : ""}`}
                                          placeholder="Enter last name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.lastName}
                                        />
                                        {validation.touched.lastName && validation.errors.lastName ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.lastName?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="email">
                                          Email <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="email"
                                          name="email"
                                          type="email"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.email && validation.errors.email ? "is-invalid" : ""}`}
                                          placeholder="Enter email address"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.email}
                                        />
                                        {validation.touched.email && validation.errors.email ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.email?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="phone">
                                          Phone
                                        </label>
                                        <input
                                          id="phone"
                                          name="phone"
                                          type="text"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter phone number"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.phone}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* RIGHT PANEL - Account & Role Information */}
                                <div className="space-y-6">
                                  
                                  {/* Account Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        🔐 Account Information
                                      </h6>
                                    </div>
                                    
                                    <div className="space-y-4">
                                      <div>
                                        <label className="form-label" htmlFor="loginId">
                                          Login ID <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="loginId"
                                          name="loginId"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.loginId && validation.errors.loginId ? "is-invalid" : ""}`}
                                          placeholder="Enter login ID"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.loginId}
                                        />
                                        {validation.touched.loginId && validation.errors.loginId ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.loginId?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="password">
                                          Password {!isEdit && <span className="text-danger">*</span>}
                                          {isEdit && <span className="text-slate-500 text-sm">(Leave blank to keep current password)</span>}
                                        </label>
                                        <div className="relative">
                                          <input
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 pr-10 ${validation.touched.password && validation.errors.password ? "is-invalid" : ""}`}
                                            placeholder={isEdit ? "Enter new password (optional)" : "Enter password"}
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.password}
                                          />
                                          <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 flex items-center pr-3"
                                            onClick={() => setShowPassword(!showPassword)}
                                          >
                                            {showPassword ? (
                                              <EyeOff className="size-4 text-slate-400" />
                                            ) : (
                                              <Eye className="size-4 text-slate-400" />
                                            )}
                                          </button>
                                        </div>
                                        {validation.touched.password && validation.errors.password ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.password?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="roleId">
                                          Role <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          id="roleId"
                                          name="roleId"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.roleId && validation.errors.roleId ? "is-invalid" : ""}`}
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.roleId}
                                        >
                                          <option value="">Select role</option>
                                          {roleOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                        {validation.touched.roleId && validation.errors.roleId ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.roleId?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label">
                                          Status
                                        </label>
                                        <div className="flex items-center gap-4">
                                          <label className="inline-flex items-center">
                                            <input
                                              type="radio"
                                              name="isActive"
                                              value="true"
                                              checked={validation.values.isActive === true}
                                              onChange={() => validation.setFieldValue("isActive", true)}
                                              className="form-radio text-custom-500"
                                            />
                                            <span className="ml-2">Active</span>
                                          </label>
                                          <label className="inline-flex items-center">
                                            <input
                                              type="radio"
                                              name="isActive"
                                              value="false"
                                              checked={validation.values.isActive === false}
                                              onChange={() => validation.setFieldValue("isActive", false)}
                                              className="form-radio text-custom-500"
                                            />
                                            <span className="ml-2">Inactive</span>
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </form>
                        )}
                        <ToastContainer closeButton={false} limit={1} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer.Body>
        
        <Drawer.Footer className="flex items-center justify-end p-4 border-t border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-600">
          <div className="flex gap-2">
            <button
              type="button"
              className="text-slate-500 btn bg-white border-slate-200 hover:text-slate-600 hover:bg-slate-100 hover:border-slate-200 focus:text-slate-600 focus:bg-slate-100 focus:border-slate-200 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-100 active:border-slate-200 active:ring active:ring-slate-100 dark:bg-zink-700 dark:hover:bg-zink-600 dark:border-zink-500 dark:text-zink-200"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="userForm"
              className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <LoaderIcon className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                  Saving...
                </>
              ) : "Save"}
            </button>
          </div>
        </Drawer.Footer>
      </Drawer>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={() => {}}
        buttonCaption="Yes, Delete It!"
      />
    </React.Fragment>
  );
};

export default AddUser;